// src/components/dashboard/QueueControls.tsx
import React, { useState, useEffect } from 'react';
import { Pause, Play, Trash2, AlertTriangle, AlertCircle } from 'lucide-react';
import { settingsService } from '../../services/settings';
import { QueueControlStatus } from '../../types/settings';
import { useAuth } from '../../contexts/AuthContext';

interface QueueControlsProps {
  onStatusChange?: () => void;
}

const QueueControls: React.FC<QueueControlsProps> = ({ onStatusChange }) => {
  // Pause and resume flip a single global setting, so they stop sending for every
  // company on the installation -- SystemAdmin only. Clearing the queue is
  // destructive, so CompanyUser (read-only) does not get it either. The API
  // enforces both; this only avoids showing buttons that would come back 403.
  const { isSystemAdmin, canEdit } = useAuth();
  const canPauseGlobally = isSystemAdmin();
  const canClearQueue = canEdit();

  const [status, setStatus] = useState<QueueControlStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showClearModal, setShowClearModal] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      setLoadError(null);
      const data = await settingsService.getQueueStatus();
      setStatus(data);
      setLoading(false);
    } catch (error: any) {
      console.error('Failed to load queue status:', error);
      setLoading(false);

      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        setLoadError('Cannot connect to API server. Is the backend running?');
      } else if (error.response?.status === 401) {
        setLoadError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        setLoadError('Your account does not have access to the queue controls.');
      } else if (error.response?.status === 404) {
        setLoadError('API endpoint not found. Check backend routing.');
      } else {
        setLoadError(error.response?.data?.error || error.message || 'Failed to load queue status');
      }
    }
  };

  const handlePause = async () => {
    setLoading(true);
    try {
      await settingsService.pauseQueue();
      showMessage('Queue processing paused', 'success');
      await loadStatus();
      onStatusChange?.();
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Failed to pause queue', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    setLoading(true);
    try {
      await settingsService.resumeQueue();
      showMessage('Queue processing resumed', 'success');
      await loadStatus();
      onStatusChange?.();
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Failed to resume queue', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearQueue = async () => {
    setLoading(true);
    setShowClearModal(false);
    try {
      // Send the count we showed in the dialog: if the queue has moved since, the
      // API refuses rather than deleting more than was agreed to. A SystemAdmin is
      // looking at every company, so the delete has to say so explicitly.
      const result = await settingsService.clearQueue(
        status?.pendingCount ?? -1,
        isSystemAdmin()
      );
      showMessage(`Queue cleared: ${result.deletedCount} emails deleted`, 'success');
      await loadStatus();
      onStatusChange?.();
    } catch (error: any) {
      if (error.response?.status === 409) {
        showMessage(
          error.response.data?.error ?? 'The queue changed while you were confirming. Try again.',
          'error'
        );
        await loadStatus();
      } else {
        showMessage(error.response?.data?.error || 'Failed to clear queue', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  // Nothing here is actionable for a read-only user, so don't render the panel.
  if (!canPauseGlobally && !canClearQueue) {
    return null;
  }

  // Show error state if loading failed
  if (loadError) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border-l-4 border-red-500">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Queue Controls Unavailable
            </h3>
            <p className="text-sm text-gray-700 mb-3">{loadError}</p>
            <button
              onClick={loadStatus}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Retry Connection
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading || !status) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        {/* Status Banner */}
        <div
          className={`px-4 py-3 rounded-lg mb-4 flex items-center gap-2 ${
            status.isPaused
              ? 'bg-amber-50 border border-amber-200 text-amber-800'
              : 'bg-green-50 border border-green-200 text-green-800'
          }`}
        >
          <div
            className={`w-3 h-3 rounded-full ${status.isPaused ? 'bg-amber-400' : 'bg-green-400'}`}
          ></div>
          <span className="font-semibold">
            Queue Processing is {status.isPaused ? 'PAUSED' : 'Running'}
          </span>
        </div>

        {/* Message */}
        {message && (
          <div
            className={`px-4 py-3 rounded-lg mb-4 ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Controls */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Queue Controls</h3>
          <div className="flex gap-3 flex-wrap">
            {canPauseGlobally &&
              (status.isPaused ? (
                <button
                  onClick={handleResume}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Play size={18} />
                  Resume Queue
                </button>
              ) : (
                <button
                  onClick={handlePause}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Pause size={18} />
                  Pause Queue
                </button>
              ))}

            {canClearQueue && (
              <button
                onClick={() => setShowClearModal(true)}
                disabled={!status.isPaused || loading}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 size={18} />
                Clear Queue
              </button>
            )}
          </div>

          {canClearQueue && (
            <p className="text-sm text-gray-600 mt-2">
              Clear Queue is only available when processing is paused
              {!canPauseGlobally && ', which a System Admin controls'}.
            </p>
          )}
        </div>
      </div>

      {/* Clear Queue Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-red-600" size={24} />
              <h3 className="text-xl font-semibold">Clear Email Queue</h3>
            </div>
            <p className="text-gray-700 mb-2">
              This will permanently delete <strong>{status.pendingCount}</strong> pending{' '}
              {status.pendingCount === 1 ? 'email' : 'emails'}
              {isSystemAdmin() ? ' across all companies' : ' for your company'}.
            </p>
            <p className="text-gray-700 mb-6">Are you sure you want to continue?</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowClearModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClearQueue}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete {status.pendingCount}{' '}
                {status.pendingCount === 1 ? 'Email' : 'Emails'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QueueControls;
